import React, { useContext, useEffect, useState } from "react";
import { Button, Form, Modal, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoaderContext } from "../../../App";
import { currentYear, EquationBuilderFor, LS_AUTHORITY_KEY, ReportedFrequency, ReportedFrequencyList, startYear } from "../../../core/constants/common";
import KPIService from "../../../core/service/KPIService";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import CompanyService from "../../../core/service/CompanyService";
import StandardService from "../../../core/service/StandardService";
import { AiOutlineCalculator } from "react-icons/ai";
import EquationBuilder from "../Standards/EquationBuilder/EquationBuilder";
import Multiselect from "multiselect-react-dropdown";
import { BsInfoCircle } from "react-icons/bs";
import './KPIGraph.scss';

function KPIModal({ t, kpiToUpdate, onHide, onSave }) {

	const year = new Date().getFullYear();
	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyId = lsData.profile.company.id;
	const { setLoaderDisplay } = useContext(LoaderContext);
	const [formData, setFormData] = useState(kpiToUpdate?.id > 0 ? kpiToUpdate : {
		ownerId: companyId,
		name: '',
		description: '',
		timelineStart: '',
		timelineEnd: '',
		measured: ReportedFrequency.YEARLY,
		target: '',
		redFlag: '',
		unit: '',
		equation: '',
		cockpitRef: null,
		assignees: [],
		dataPoints: [],
		goalId: kpiToUpdate.goalId ?? 0,
		weight: '',
		baseYear: '',
		baseValue: ''
	});
	const [validated, setValidated] = useState(false);
	const [goals, setGoals] = useState([]);
	const [entities, setEntities] = useState([]);
	const [uomList, setUOMList] = useState([]);
	const [eqnModalDisplay, setEqnModalDisplay] = useState(false);
	const [selectedType, setSelectedType] = useState(
		kpiToUpdate.baseYear !== null && kpiToUpdate.baseValue === null
			? 'baseYear'
			: kpiToUpdate.baseValue !== null
				? 'baseYearValue'
				: 'baseYear'
	);
	const yearOptions = [];
	yearOptions.push(<option value="" key="placeholder" disabled selected={formData.baseYear === null}>{t("Select.label")}</option>);
	for (let i = currentYear; i >= startYear; i--) {
		yearOptions.push(<option value={i} key={i}>{i}</option>);
	}

	useEffect(() => {
		fetchGoals();
		fetchEntities();
		getUOMs();
	}, []);

	const fetchGoals = () => {
		KPIService.listGoals(companyId).then(res => {
			if (res.status !== 200) return;

			setGoals(res.data.data ?? []);
		}).catch(err => { });
	};

	const fetchEntities = () => {
		CompanyService.getEnterpriseStructure()
			.then(res => {
				if (res.status !== 200) return;

				const nodes = [];
				populateEntities(res.data.data ?? [], nodes);
				setEntities(nodes);
			})
			.catch(err => {
			});
	};

	const populateEntities = (children, nodes) => {
		children?.forEach(child => {
			nodes.push(child);
			if (child?.children?.length > 0) {
				populateEntities(child.children, nodes);
			}
		});
	};

	const getUOMs = () => {
		StandardService.getUnitsOfMeasure()
			.then(res => {
				if (res.status !== 200) return;

				const uniqueInputArray = Array.from(new Set(res.data.data?.map(obj => obj.inputUnit))).map(
					value => res.data.data?.find(obj => obj.inputUnit === value)
				);
				uniqueInputArray.sort((a, b) => a.inputUnit.localeCompare(b.inputUnit));
				setUOMList(uniqueInputArray);
			})
			.catch((error) => { });
	};

	const saveData = (e) => {
		setValidated(true);
		e.preventDefault();
		const goalForm = e.currentTarget;
		if (goalForm.checkValidity() === false || !formData.equation) {
			e.stopPropagation();
			toast.warning('Please fill all the mandatory fields');
			return;
		}
		setLoaderDisplay(true);
		const fData = JSON.parse(JSON.stringify(formData));
		if (fData.id === 0) {
			delete fData.id;
		}
		KPIService.upsertKPI(fData, fData.goalId).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200 && res.status !== 201) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			onSave(fData.goalId);
		}).catch((error) => {
			setLoaderDisplay(false);
		});
	};


	const renderEquationField = () => {
		return (
			<div className="col-md-12 p-2">
				<div className="mb-1">{t("CreateAnEquationWithMetricsKPI.label")} <span className="text-danger">*</span></div>
				<div className="AddEquation_box 1">
					<span className={`cursor w-100 ${validated && !formData.equation ? 'border border-danger' : ''}`}
						onClick={() => setEqnModalDisplay(true)}>
						<AiOutlineCalculator className="me-2" />
						{formData.equation ? formData.equation.replace(/[DM]/g, '') : t("CreateEquation.label")}
					</span>
					{
						eqnModalDisplay &&
						<EquationBuilder
							buildFor={EquationBuilderFor.KPI}
							equationObj={{ expression: formData.equation.replace(/[DM]/g, ''), expressionWithId: formData.equation }}
							discardBtnClick={() => setEqnModalDisplay(false)}
							updatedEquationObj={(val) => onEquationUpdate(val)} />
					}
				</div>
			</div>
		)
	};

	const onEquationUpdate = (eq) => {
		const d = JSON.parse(JSON.stringify(formData));
		d.equation = eq.expressionWithId;
		const dps = JSON.parse(JSON.stringify(eq.selectedDPs));
		if (kpiToUpdate?.id > 0) {
			dps?.forEach(dp => {
				const matchingDPIndex = d.dataPoints?.findIndex(eDP => eDP.id === dp.id);
				if (matchingDPIndex < 0) {
					delete dp.id;
				}
			});
		} else {
			dps?.forEach(dp => delete dp.id);
		}
		d.dataPoints = dps;
		d.cockpitRef = eq.selectedMetricIds.toString();
		setFormData(d);
		setEqnModalDisplay(false);
	};

	const onSelectDeSelectEntities = (selectedItem) => {
		const assignees = [...formData.assignees];
		const index = formData.assignees.findIndex(a => a.assigneeId === selectedItem.id);
		if (index >= 0) {
			assignees.splice(index, 1);
		} else {
			assignees.push({
				assigneeId: selectedItem.id,
				assigneeName: selectedItem.name
			});
		}
		setFormData({
			...formData,
			assignees: assignees
		});
	};

	const getSelectedEntities = (assignees) => {
		const data = [];
		assignees?.forEach(a => {
			data.push({ id: a.assigneeId, name: a.assigneeName });
		});
		return data;
	};



	return (
		<>
			<Modal show={true} onHide={() => onHide()}
				aria-labelledby="contained-modal-title-vcenter"
				size="lg"
				centered>
				<Modal.Header closeButton className="border-0">
					< Modal.Title as="h5">{kpiToUpdate?.id > 0 ? t('UpdatKeyPerformanceIndicator.label') : t('AddKeyPerformanceIndicator.label')}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="AddKPI_ModalBody">
					<Form noValidate validated={validated} onSubmit={saveData}>
						<div className="row m-0">
							<div className="col-md-6 p-2">
								<label className="mb-1">{t("DefineKPI.label")} <span className="text-danger">*</span></label>
								<input
									type="text"
									className="form-control"
									onChange={(e) => setFormData({ ...formData, name: e.target.value })}
									value={formData.name}
									placeholder={t("AnnualCarbonEmissionsReduction.label")}
									required />
							</div>
							<div className="col-md-6 p-2">
								<div className="d-flex justify-content-between">
									<label className="mb-1">{t("GoalRelatedtTTheKPI.label")} <span className="text-danger">*</span></label>
								</div>
								<select className="form-select"
									required
									value={formData.goalId}
									onChange={(e) => setFormData({ ...formData, goalId: +e.target.value })}>
									<option value='' key=''>{t("Select.label")}</option>
									{
										goals?.map((goal) => (
											<option value={goal.id} key={goal.id}>{goal.name}</option>
										))
									}
								</select>
							</div>
							<div className="col-md-12 p-2">
								<div className="d-flex justify-content-between">
									<label htmlFor="desc" className="mb-1">{t("Description.label")}</label>
									<span className="font-italic text-secondary">Max 250 Words</span>
								</div>
								<textarea
									className="form-control"
									rows={5}
									onChange={(e) => setFormData({ ...formData, description: e.target.value })}
									defaultValue={formData.description}
									placeholder={t("AnnualCarbonEmissionsReductionEachYear.label")}
								></textarea>
							</div>
						
							<div className="col-lg-4 p-2">
								<div className="d-flex justify-content-between">
									<label className="mb-1">{t("Weightage.label")} <span className="text-danger">*</span></label>
									<span className="font-italic text-secondary">{t("Max.label")} 100.00</span>
								</div>
								<input
									type="number"
									className="form-control"
									onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) })}
									value={formData.weight}
									step="0.01"
									min="0"
									max="100.00"
									required />
							</div>
							<div className="col-lg-4 p-2">
								<Form.Check
									type="radio"
									inline
									name="typeck"
									id="baseYear"
									label={t("BaseYear.label")}
									className="DataTypeCheck mb-1"
									checked={selectedType === 'baseYear'}
									onChange={(e) => setSelectedType(e.target.id)}
								/>
								{
									<select disabled={selectedType === 'baseYearValue'} className="form-select" value={formData.baseYear ? new Date(formData.baseYear).getFullYear() : ""} onChange={(e) => { setFormData({ ...formData, baseYear: `${e.target.value}-01-01`, baseValue: '' }); }}>
										{yearOptions}
									</select>
								}
							</div>
							<div className="col-lg-4 p-2">
								<Form.Check
									type="radio"
									inline
									name="typeck"
									id="baseYearValue"
									label={t("BaseYearValue.label")}
									className="DataTypeCheck mb-1"
									checked={selectedType === 'baseYearValue'}
									onChange={(e) => setSelectedType(e.target.id)}
								/>
								{
									<input
										disabled={selectedType === 'baseYear'}
										type="number"
										className="form-control"
										onChange={(e) => setFormData({ ...formData, baseValue: parseFloat(e.target.value), baseYear: '' })}
										value={formData.baseValue}
										step="0.01"
										min="0" />
								}
							</div>
						
							<div className="col-lg-12 px-2 pt-2">
								<h6 className=" m-0">{t('Measurement.label')} </h6>
							</div>
							<div className="col-lg-4 p-2">
								<label className="mb-1">{t('TimelineToAchieveTheKPI.label')} <span className="text-danger">*</span></label>
								<ReactDatePicker
									selected={formData.timelineStart ? new Date(formData.timelineStart) : null}
									onChange={(date) => {
										setFormData({
											...formData,
											timelineStart: moment(date).format('YYYY-MM-DD')
										});
									}}
									className="form-control"
									dateFormat="dd/MM/yyyy"
									placeholderText={t("Startdate.label")}
									required />
							</div>
							<div className="col-lg-4 p-2">
								<label className="mb-1">&nbsp;</label>
								<ReactDatePicker
									selected={formData.timelineEnd ? new Date(formData.timelineEnd) : null}
									onChange={(date) => {
										setFormData({
											...formData,
											timelineEnd: moment(date).format('YYYY-MM-DD')
										});
									}}
									className="form-control"
									dateFormat="dd/MM/yyyy"
									placeholderText={t("Enddate.label")}
									minDate={formData.timelineStart ? new Date(formData.timelineStart) : new Date()}
									required />
							</div>
							<div className="col-lg-4 p-2">
								<label className="mb-1">{t("Measured.label")} <span className="text-danger">*</span></label>
								<select className="form-select"
									required
									value={formData.measured}
									onChange={(e) => {
										setFormData({
											...formData,
											measured: e.target.value
										});
									}}>
									<option value='' key=''>{t("Select.label")}</option>
									{
										ReportedFrequencyList?.map((f) => (
											<option value={f.key} key={f.key}>{f.text}</option>
										))
									}
								</select>
							</div>
						</div>

						{renderEquationField()}

						<div className="row m-0">
							<div className="col-lg-12 px-2 pt-2">
								<h6 className=" m-0">{t('KPIRange.label')} </h6>
							</div>
							<div className="col-lg-4 p-2">
								<label className="mb-1">
									{t('Target.label')} <span className="text-danger">*</span>
									<OverlayTrigger
										key="top"
										placement="top"
										overlay={
											<Tooltip id="tooltip_1">
												<strong> {t('Target.label')} : </strong> {t("KPITargetToolTip.label")}
											</Tooltip>
										}
									>
										<span className="ms-2"><BsInfoCircle /></span>
									</OverlayTrigger>
								</label>
								<input
									type="number"
									className="form-control"
									onChange={(e) => setFormData({ ...formData, target: e.target.value })}
									value={formData.target}
									required />
							</div>
							<div className="col-lg-4 p-2">
								<label className="mb-1">{t('RedFlag.label')} <span className="text-danger">*</span>
									<OverlayTrigger
										key="top"
										placement="top"
										overlay={
											<Tooltip id="tooltip_1">
												<strong> {t('RedFlag.label')} : </strong> {t("KPIRedFlagToolTip.label")}
											</Tooltip>
										}
									>
										<span className="ms-2"><BsInfoCircle /></span>
									</OverlayTrigger>
								</label>
								<input
									type="number"
									className="form-control"
									onChange={(e) => setFormData({ ...formData, redFlag: e.target.value })}
									value={formData.redFlag}
									required />
							</div>
							<div className="col-lg-4 p-2">
								<label className="mb-1">{t("MeasuringUnit.label")}</label>
								<select className='form-select'
									id='unitSelector'
									value={formData.unit}
									onChange={(e) => setFormData({ ...formData, unit: e.target.value })}>
									<>
										<option key='' value=''>{t("Select.label")}</option>
										{
											uomList?.map(u => {
												return <option key={u.id} value={u.inputUnit}>{u.inputUnit}</option>;
											})
										}
									</>
								</select>
							</div>
						</div>

						<div className="px-2 pt-2">
							<h6 className=" m-0">{t('AssignTheResponsibilityForTrackingKPIEntities.label')} </h6>
						</div>

						<div className="p-2">
							<label className="mb-1">
								{t("SelectEntities.label")}
							</label>
							<Multiselect
								placeholder={t("Select.label")}
								options={entities}
								selectedValues={getSelectedEntities(formData.assignees)}
								onSelect={(selectedList, selectedItem) => {
									onSelectDeSelectEntities(selectedItem);
								}}
								onRemove={(selectedList, selectedItem) => {
									onSelectDeSelectEntities(selectedItem);
								}}
								displayValue="name"
								showArrow={false}
							/>
						</div>
						<div className="text-center p-2">
							<Button variant="secondary" className="mx-2" onClick={() => onHide()}>{t('Cancel.label')}</Button>
							<button type="submit" className="btn btn-primary px-4">{t('Save.label')}</button>
						</div>
					</Form>
				</Modal.Body >
			</Modal >
		</>
	);
}

export default withTranslation()(KPIModal);

